import EntityModel from '@/common/models/entity-model';
import {DateTimeFormats} from '@/common/datetime-formats';
import _ from '@/common/helpers';

export default class ScheduleInstructionModel extends EntityModel {
    hydrate({
        method = null,
        ...instructionDetails
    } = {}) {
        this.method = method;
        if ([
            ScheduleInstructionModel.Methods.intelligent,
            ScheduleInstructionModel.Methods.dateInterval,
        ].includes(this.method)) {
            ({duration: this.duration = 1, unit: this.unit} = instructionDetails);
        } else if (this.method === ScheduleInstructionModel.Methods.dayOfMonth) {
            ({day: this.day, time: this.time} = instructionDetails);
            this.time = this.time ? ScheduleInstructionModel.timeToMoment(this.time) : null;
        } else if (this.method === ScheduleInstructionModel.Methods.dayOfWeek) {
            ({
                day: this.day,
                week: this.week = ScheduleInstructionModel.WeekTypes.next,
                time: this.time,
            } = instructionDetails);
            this.time = this.time ? ScheduleInstructionModel.timeToMoment(this.time) : null;
        }
    }

    static Methods = {
        auto: 'auto',
        dateInterval: 'date-interval',
        dayOfMonth: 'day-of-month',
        dayOfWeek: 'day-of-week',
        immediately: 'immediately',
        intelligent: 'intelligent',
    };

    static InvoiceRetryMethods = {
        dateInterval: 'date-interval',
        dayOfMonth: 'day-of-month',
        dayOfWeek: 'day-of-week',
        immediately: 'immediately',
        intelligent: 'intelligent',
    };

    static CommonEventMethods = {
        dateInterval: 'date-interval',
        dayOfMonth: 'day-of-month',
        dayOfWeek: 'day-of-week',
    };

    static Units = {
        day: 'day',
        days: 'days',
        hour: 'hour',
        hours: 'hours',
        minute: 'minute',
        minutes: 'minutes',
        month: 'month',
        months: 'months',
        second: 'second',
        seconds: 'seconds',
        year: 'year',
        years: 'years',
    };

    static WeekTypes = {
        next: 'next',
        firstInMonth: 'first-in-month',
        lastInMonth: 'last-in-month',
    };

    get weekLabel() {
        if (this.week === ScheduleInstructionModel.WeekTypes.firstInMonth) {
            return 'first';
        } if (this.week === ScheduleInstructionModel.WeekTypes.lastInMonth) {
            return 'last';
        }
        return this.week;
    }

    getPayload() {
        const payload = {
            method: this.method,
        };
        if ([
            ScheduleInstructionModel.Methods.intelligent,
            ScheduleInstructionModel.Methods.dateInterval,
        ].includes(this.method)) {
            payload.duration = Number(this.duration);
            payload.unit = this.unit;
        } else if (this.method === ScheduleInstructionModel.Methods.dayOfMonth) {
            payload.day = this.day;
            payload.time = this.time
                ? this.time.format(ScheduleInstructionModel.timeAPIFormat)
                : null;
        } else if (this.method === ScheduleInstructionModel.Methods.dayOfWeek) {
            payload.day = this.day;
            payload.time = this.time
                ? this.time.format(ScheduleInstructionModel.timeAPIFormat)
                : null;
            payload.week = this.week;
        }
        return payload;
    }

    get unitLabel() {
        return _.pluralize(this.unit.replace(/s$/, ''), this.duration);
    }

    get summary() {
        const getTime = () => {
            if (this.time) {
                return this.time.format(DateTimeFormats.timelineTime);
            }
            return `the same time this action was triggered`;
        };
        switch (this.method) {
            case ScheduleInstructionModel.Methods.auto:
            default:
                return `automatically`;
            case ScheduleInstructionModel.Methods.dateInterval:
                return `${this.duration} ${this.unitLabel} later`;
            case ScheduleInstructionModel.Methods.intelligent:
                return `intelligently within ${this.duration} ${this.unitLabel}`;
            case ScheduleInstructionModel.Methods.dayOfMonth:
                return `on the ${_.ordinal(this.day)} day of the month at ${getTime()}`;
            case ScheduleInstructionModel.Methods.dayOfWeek:
                return `on the ${this.weekLabel} ${this.day} at ${getTime()}`;
        }
    }
}
